import {isLinebreak, LocalizedBold, LocalizedItem, LocalizedLink, TextChildren} from "./CommonTextProps";
import {InlineLink} from "./InlineLink";
import {Fragment, isValidElement} from "react";
import {Children, intercalateChildren} from "../Children";

function isBoldText(children: TextChildren): children is LocalizedBold {
    return (
        typeof children === "object" &&
        "boldText" in children &&
        !!children.boldText
    );
}

function isLink(children: TextChildren): children is LocalizedLink {
    return (
        typeof children === "object" &&
        ("href" in children || "onClick" in children)
    );
}

function isArray(
    children: TextChildren
): children is [LocalizedItem, ...LocalizedItem[]] {
    return (
        typeof children === "object" && typeof (children as any).length === "number"
    );
}

function textChildrenToChildrenArray(children: TextChildren, activeAccessibleElements?: boolean): Array<Children> {
    if (isArray(children)) {
        return children.flatMap((children) => textChildrenToChildrenArray(children, activeAccessibleElements));
    } else if (typeof children === "string") {
        return [activeAccessibleElements?<span aria-hidden>{children}</span> :<>{children}</>];
    } else if (isBoldText(children)) {
        return [
            <span style={{fontWeight: "500"}} {...(activeAccessibleElements && {"aria-hidden": true})}>
        {children.boldText}
      </span>
        ];
    } else if (isLink(children)) {
        if (children.href != null) {
            return [
                <InlineLink href={children.href} disabled={children.disabled} ariaLabel={children.ariaLabel}>
                    {children.label}
                </InlineLink>
            ];
        } else {
            return [
                <InlineLink onClick={children.onClick} disabled={children.disabled} ariaLabel={children.ariaLabel}>
                    {children.label}
                </InlineLink>
            ];
        }
    } else if (isLinebreak(children)) {
        return [<br aria-hidden={activeAccessibleElements} />];
    } else if (isValidElement(children)) {
        return [<span>{children}</span>];
    } else {
        throw new Error("invalid TextChildren");
    }
}

export function textChildrenToChildren(children: TextChildren, separator: string = " ", noNbspSeparator: boolean = false, activeAccessibleElements?: boolean): Children {
    const textChildren = textChildrenToChildrenArray(children, activeAccessibleElements);
    const lines = splitBy(e => isValidElement(e) && e.type === "br")(textChildren);

    const mapLine = (line: unknown[]) => {
        return line.map((child, i) => <Fragment key={i}>{child}</Fragment>);
    }

    const mappedResult = lines.map(line =>
        intercalateChildren(
            separator === " " ? noNbspSeparator?<> </>:<>&nbsp;</> : <>{separator}</>,
            mapLine(line)
        )
    );

    const finalResult: Children[] = mappedResult.reduce((acc, curr, index) => {
        if (index > 0) {
            acc.push(<br/> as Children);
        }
        acc.push(curr);
        return acc;
    }, [] as Children[]);

    return finalResult;
}

function splitBy<A>(predicate: (a: A) => boolean): (arr: A[]) => A[][] {
    return function (arr: A[]): A[][] {
        // Find the longest prefix where the predicate is false.
        let i = 0;
        while (i < arr.length && !predicate(arr[i])) {
            i++;
        }
        const init = arr.slice(0, i);
        const rest = arr.slice(i);

        // If there's no delimiter element (rest is empty), return the initial segment.
        if (rest.length === 0) {
            return [init];
        } else {
            // Otherwise, drop the delimiter (first element of rest) and recursively process the tail.
            const tail = rest.slice(1);
            return [init, ...splitBy(predicate)(tail)];
        }
    }
}